import { createFileRoute, Link } from "@tanstack/react-router";
import { SubPage } from "@/components/sub-page";

const URL = "https://mine-class.com/guias/acreditacion-codelco";
const TITLE = "Acreditación Codelco: Guía Completa para Contratistas";
const DESCRIPTION =
  "Guía práctica de acreditación Codelco y certificación minera en Chile: requisitos, pasos, documentación y capacitación OTEC para contratistas.";
const PUBLISHED = "2026-07-14";

export const Route = createFileRoute("/guias/acreditacion-codelco")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "keywords", content: "acreditación codelco, certificación minera chile, capacitación minera, otec minería, contratistas codelco" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { property: "article:published_time", content: PUBLISHED },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESCRIPTION,
          datePublished: PUBLISHED,
          dateModified: PUBLISHED,
          mainEntityOfPage: URL,
          author: { "@type": "Organization", name: "Mine Class" },
          publisher: {
            "@type": "Organization",
            name: "Mine Class",
            url: "https://mine-class.com/",
          },
        }),
      },
    ],
  }),
  component: GuiaAcreditacionCodelco,
});

function GuiaAcreditacionCodelco() {
  return (
    <SubPage
      eyebrow="Guía"
      title="Acreditación Codelco y certificación minera en Chile"
      intro="Todo lo que un contratista o profesional necesita saber sobre los requisitos, pasos y capacitación para operar en faenas de Codelco."
    >
      <p>
        Trabajar como contratista en <strong>Codelco</strong> —la mayor productora de cobre del mundo—
        exige cumplir con un estricto proceso de <strong>acreditación</strong> que combina requisitos
        legales, técnicos, de salud ocupacional y de <strong>capacitación minera</strong> certificada.
        Esta guía resume, desde nuestra experiencia como OTEC especializada en la industria minera, qué
        implica hoy la acreditación Codelco y cómo prepararse para aprobarla sin fricciones.
      </p>

      <h2>¿Qué es la acreditación Codelco?</h2>
      <p>
        La acreditación Codelco es el proceso mediante el cual una empresa contratista y sus
        trabajadores demuestran que cumplen los estándares de seguridad, salud, competencias técnicas
        y cumplimiento normativo exigidos para ingresar a faena. No es un trámite único: se compone de
        acreditación de empresa, acreditación de personas y validación permanente de documentos y
        certificaciones.
      </p>

      <h2>Requisitos generales para contratistas</h2>
      <ul>
        <li>
          <strong>Documentación empresa:</strong> iniciación de actividades, RUT, certificados de
          antecedentes laborales y previsionales (F30 y F30-1), pólizas y seguros vigentes.
        </li>
        <li>
          <strong>Reglamento interno de higiene y seguridad</strong> actualizado según la Ley 16.744 y
          protocolos MINSAL (PLANESI, PREXOR, PSICOSOCIAL, entre otros).
        </li>
        <li>
          <strong>Sistema de gestión SST</strong> alineado con los estándares de control de fatalidades
          (ECF) de Codelco y su matriz de riesgos críticos.
        </li>
        <li>
          <strong>Personal acreditado:</strong> exámenes preocupacionales y ocupacionales al día,
          licencias y credenciales específicas por rol (altura física, espacios confinados, izaje,
          conducción interna, etc.).
        </li>
        <li>
          <strong>Capacitación obligatoria:</strong> inducción hombre nuevo, curso de reglas por la
          vida, riesgos críticos y cursos específicos por división (Chuquicamata, El Teniente,
          Radomiro Tomic, Andina, Salvador, Ministro Hales, Gabriela Mistral).
        </li>
      </ul>

      <h2>Pasos para acreditar a un trabajador</h2>
      <ol>
        <li>
          <strong>Contrato y afiliaciones previsionales</strong> vigentes (AFP, Isapre/Fonasa, mutual
          de seguridad).
        </li>
        <li>
          <strong>Exámenes ocupacionales</strong> según el rol y las condiciones de la faena
          (altitud geográfica, altura física, ruido, sílice, psicosensotécnico, etc.).
        </li>
        <li>
          <strong>Cursos obligatorios</strong> aprobados en una OTEC autorizada por Sence y
          reconocida por Codelco, con evidencia trazable de aprendizaje y evaluación.
        </li>
        <li>
          <strong>Carga documental en el sistema de acreditación</strong> del mandante o del
          integrador utilizado por la faena.
        </li>
        <li>
          <strong>Emisión de credencial</strong> o pase digital que permite el ingreso a la faena y
          se revalida periódicamente.
        </li>
      </ol>

      <h2>Certificación minera en Chile: marco regulatorio</h2>
      <p>
        La certificación minera en Chile se ancla en la Ley 16.744 (accidentes del trabajo), el
        Decreto Supremo 132 (reglamento de seguridad minera de Sernageomin), la NCh2827:2015
        (calidad para OTEC) y los estándares particulares del mandante. Codelco añade sus propios
        Estándares de Control de Fatalidades y protocolos internos de contratistas.
      </p>
      <p>
        Para que un curso sea válido en la acreditación, debe ser dictado por una OTEC autorizada,
        con relatores acreditados, contenidos aprobados y sistema de evaluación auditable. La
        certificación resultante debe incluir código verificable, identidad validada del alumno y
        respaldo digital.
      </p>

      <h2>Cursos y certificaciones más solicitados</h2>
      <ul>
        <li>Inducción hombre nuevo y reglas por la vida.</li>
        <li>Riesgos críticos y estándares de control de fatalidades.</li>
        <li>Trabajos en altura física, espacios confinados y aislación y bloqueo (LOTO).</li>
        <li>Manejo defensivo y conducción interna en faena.</li>
        <li>Uso y mantención de EPP, sustancias peligrosas, izaje y rigging.</li>
        <li>Protocolos MINSAL: sílice (PLANESI), ruido (PREXOR), riesgos psicosociales.</li>
      </ul>

      <h2>Cómo Mine Class facilita el proceso</h2>
      <p>
        Como OTEC minera con más de una década trabajando junto a grandes mandantes,{" "}
        <Link to="/quienes-somos/que-es">Mine Class</Link> integra en una sola plataforma la
        capacitación, la validación biométrica de identidad y el pasaporte digital de acreditación.
        Nuestro ecosistema cubre el ciclo completo:
      </p>
      <ul>
        <li>
          <Link to="/soluciones/tecnologia-aprendizaje/athena-lms">Athena LMS</Link>: cursos mineros
          mobile-first con anti-trampa IA para asegurar aprendizaje verificable.
        </li>
        <li>
          <Link to="/soluciones/tecnologia-aprendizaje/face-id">FaceID</Link>: validación biométrica
          en tiempo real de la identidad del alumno durante cada evaluación.
        </li>
        <li>
          <Link to="/soluciones/acreditacion-contratistas/mine-pass">Mine Pass</Link>: pasaporte
          digital que centraliza documentos, exámenes y certificados con IA que anticipa vencimientos
          y bloqueos de ingreso.
        </li>
        <li>
          <Link to="/soluciones/acreditacion-contratistas/examine">Examine</Link>: agendamiento y
          gestión de exámenes preocupacionales y ocupacionales.
        </li>
      </ul>

      <h2>Errores frecuentes al acreditar contratistas</h2>
      <ul>
        <li>Enviar exámenes vencidos o sin la batería requerida por el cargo.</li>
        <li>Cursos dictados por relatores no acreditados o sin evaluación auditable.</li>
        <li>Documentación de empresa (F30, F30-1, pólizas) desactualizada.</li>
        <li>Trabajadores sin trazabilidad de identidad durante la capacitación online.</li>
        <li>Reprocesos por no anticipar la caducidad de credenciales.</li>
      </ul>

      <h2>Próximos pasos</h2>
      <p>
        Si tu empresa acredita contratistas para Codelco u otros mandantes de la gran minería y
        buscas reducir tiempos, evitar rechazos y digitalizar el proceso completo,{" "}
        <Link to="/contacto/formulario">agenda una demo con nuestro equipo</Link>. Te mostramos cómo
        implementar acreditación digital, capacitación certificada y trazabilidad biométrica en un
        solo lugar.
      </p>
    </SubPage>
  );
}
